<template>
    <DropdownMenu>
        <DropdownMenuTrigger
            as-child
            class="cursor-pointer"
        >
            <Avatar :user-name="userName" />
        </DropdownMenuTrigger>

        <DropdownMenuContent
            class="w-56"
            align="end"
        >
            <DropdownMenuSub v-if="userStore.accounts.length > 1">
                <DropdownMenuSubTrigger>
                    <UserIcon />
                    {{ $t('NAVIGATION.ACCOUNT') }}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                        <DropdownMenuRadioGroup
                            v-model="userStore.account.id"
                            @update:model-value="(id) => updateSelectedAccount(id as string)"
                        >
                            <DropdownMenuRadioItem
                                v-for="account in userStore.accounts"
                                :key="account.id"
                                :value="account.id"
                            >
                                {{ account.name }}
                            </DropdownMenuRadioItem>
                        </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuItem as-child>
                <router-link to="/settings">
                    <Cog6ToothIcon />
                    {{ $t('NAVIGATION.ACCOUNT_SETTINGS') }}
                </router-link>
            </DropdownMenuItem>

            <DropdownMenuSub>
                <DropdownMenuSubTrigger>
                    <LanguageIcon />
                    {{ $t('GLOBAL.LANGUAGE') }}
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                    <DropdownMenuSubContent>
                        <DropdownMenuRadioGroup v-model="locale">
                            <DropdownMenuRadioItem
                                v-for="lang in languageService.languages"
                                :key="lang.code"
                                :value="lang.code"
                            >
                                {{ lang.full }}
                            </DropdownMenuRadioItem>
                        </DropdownMenuRadioGroup>
                    </DropdownMenuSubContent>
                </DropdownMenuPortal>
            </DropdownMenuSub>

            <DropdownMenuSeparator />

            <DropdownMenuItem @click="authService.logout(redirect)">
                <ArrowLeftStartOnRectangleIcon />
                {{ $t('GLOBAL.LOGOUT') }}
            </DropdownMenuItem>

            <template v-if="appVersion && userStore.isSupport">
                <DropdownMenuSeparator />
                <DropdownMenuItem disabled>
                    <span class="capitalize flex-1">{{ $t('GLOBAL.VERSION') }}</span>
                    {{ '1.0.1' }}
                </DropdownMenuItem>
            </template>
        </DropdownMenuContent>
    </DropdownMenu>
</template>

<script lang="ts" setup>
    import { Cog6ToothIcon, ArrowLeftStartOnRectangleIcon, LanguageIcon, UserIcon } from '@heroicons/vue/24/outline';

    import {
        DropdownMenu,
        DropdownMenuContent,
        DropdownMenuItem,
        DropdownMenuPortal,
        DropdownMenuSeparator,
        DropdownMenuSub,
        DropdownMenuSubContent,
        DropdownMenuSubTrigger,
        DropdownMenuTrigger,
        DropdownMenuRadioGroup,
        DropdownMenuRadioItem,
    } from '@/shared/ui/DropdownMenu';

    import authService from '@/services/auth.service';
    import { useUserStore } from '@/stores/user.store';

    import Avatar from '@/shared/ui/Avatar/Avatar.vue';
    import languageService from '@/services/language.service';
    import userService from '@/services/user.service';

    const { locale } = useI18n();

    const router = useRouter();
    const active = ref<boolean>(false);
    const userName = computed<string>(() => `${userStore.user.first_name} ${userStore.user.last_name}`);
    const redirect = computed(() => (userStore.isAdmin ? false : import.meta.env.PROD));
    const userStore = useUserStore();
    const appVersion = import.meta.env.VUE_APP_COMMIT_REF;

    watch(locale, async (value) => {
        languageService.setLocaleLanguage(value);
        if (userStore.user) {
            await userService.updateUser({ locale: value as any });
        }
    });

    const updateSelectedAccount = (accountId: string) => {
        userStore.setCurrentAccount(accountId);
        active.value = false;

        router.replace({ name: 'dashboard' });
    };
</script>
